import { Switch } from "@mantine/core";
import type { CellContext } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  IndividualScreenerLocationStatus,
  type IndividualScreenerLocationStatusType,
  type ScreenerLocation,
} from "@/types/screener-location";
import { locationIsLive } from "@/utils/screener-location.utils";
import { stopPropagationWrapper } from "@/utils/ui";
import { useUpdateScreenerLocationStatusMutation } from "@/hooks/queries/screener-locations.queries";
import { useWarningModal } from "@/hooks/warning-modal-hook";

interface LocationStatusCellProps {
  info: CellContext<ScreenerLocation, IndividualScreenerLocationStatusType>;
}

export const LocationStatusCell = ({ info }: LocationStatusCellProps) => {
  const track = useTrackAnalytics();
  const { mutate: updateStatus } = useUpdateScreenerLocationStatusMutation();

  const [status, isLive] = useMemo(() => {
    const status = info.getValue();
    return [status, locationIsLive(status)] as const;
  }, [info]);

  const updateLocationStatus = useCallback(
    (newStatus: IndividualScreenerLocationStatusType) => {
      track("Status Toggled", {
        location_id: info.row.original.id,
        status: newStatus,
      });

      updateStatus({ locationId: info.row.original.id, status: newStatus });
    },
    [info, track, updateStatus],
  );

  const {
    warningModal: pauseWarningModal,
    openWarningModal: openPauseConfirmation,
  } = useWarningModal({
    title: "Pause Location",
    text: "Are you sure you want to pause this location? Candidates who try to access it will be shown an error until you unpause.",
    confirmText: "Pause Location",
    cancelText: "Cancel",
    onConfirm: () => {
      updateLocationStatus(IndividualScreenerLocationStatus.Paused);
    },
  });

  const {
    warningModal: unPauseWarningModal,
    openWarningModal: openUnPauseConfirmation,
  } = useWarningModal({
    title: "Unpause Location",
    text: "Are you sure you want to unpause this location? Once you do, candidates will be able to access it via the share link.",
    confirmText: "Unpause Location",
    cancelText: "Cancel",
    onConfirm: () => {
      updateLocationStatus(IndividualScreenerLocationStatus.Live);
    },
  });

  const handleChange = useCallback(() => {
    if (status === IndividualScreenerLocationStatus.Live) {
      openPauseConfirmation();
    } else {
      openUnPauseConfirmation();
    }
  }, [status, openPauseConfirmation, openUnPauseConfirmation]);
  // Wrapper div onClick to prevent the click from bubbling up to the row click
  // handler. The Mantine onClick seems to be fired from a different element
  // making stopping propagation within the Switch change handler infeasible
  return (
    <div onClick={stopPropagationWrapper()}>
      <Switch checked={isLive} label={status} onClick={handleChange} />
      {pauseWarningModal}
      {unPauseWarningModal}
    </div>
  );
};
